// engineClient.js — main-thread API for the drywall takeoff engine.
//   const eng = createEngine(pdfjsLib)
//   await eng.load(pageNum, page)                        // extract vectors (main thread) → worker
//   await eng.roomAt(pageNum, ftPerUnit, x, y)           // click-to-fill
//   await eng.rooms(pageNum, ftPerUnit, region?)         // every enclosed room (optionally in view)
//   await eng.grids(pageNum, ftPerUnit, rings)           // ceiling grid (2x4 / 2x2 / none) inside each room
// Coordinates are sheet units (page.getViewport({scale:1}) px). Falls back to running inline if a
// Web Worker can't be created (old bundler, CSP).
import { extractSheet } from "./pdfVectors.js";
import { detectScaleFromText } from "./scaleText.js";
import { checkSheetScale, dimensionLines } from "./scaleCheck.js";

export function createEngine(pdfjsLib) {
  let worker = null,
    inline = null,
    seq = 0;
  const pending = new Map();
  const loaded = new Map(); // pageNum → { scale, textCount }
  const checks = new Map(); // pageNum → checkSheetScale result (kept after the sheet is dropped)

  try {
    worker = new Worker(new URL("./engine.worker.js", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (ev) => {
      const { id, ok, result, error } = ev.data;
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      ok ? p.resolve(result) : p.reject(new Error(error));
    };
    worker.onerror = (e) => {
      console.warn("takeoff engine worker failed, running inline", e?.message);
      worker = null;
    };
  } catch {
    worker = null;
  }

  async function call(op, key, args, transfer) {
    if (worker) {
      const id = ++seq;
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        worker.postMessage({ id, op, key, args }, transfer || []);
      });
    }
    // inline fallback (same code, main thread)
    if (!inline) {
      const W = await import("./wallEngine.js");
      inline = { W, sheets: new Map() };
    }
    const { W, sheets } = inline;
    const slimRuns = (st) =>
      st.runs.map(({ id, ux, uy, nx, ny, c, th, t0, t1 }) => ({
        id,
        ux,
        uy,
        nx,
        ny,
        c,
        th,
        t0,
        t1,
      }));
    const ensure = (k, f, o) => {
      const e = sheets.get(k);
      if (!e) throw new Error("sheet not loaded");
      const ok = JSON.stringify(o || {});
      if (!e.st || e.f !== f || e.o !== ok) {
        e.st = W.analyzeSheet(e.sheet, f, o || {});
        e.f = f;
        e.o = ok;
      }
      return e.st;
    };
    const dimsOf = (k) => {
      const e = sheets.get(k);
      if (!e) return [];
      if (!e.dims) {
        try {
          e.dims = dimensionLines(e.sheet);
        } catch {
          e.dims = [];
        }
      }
      return e.dims;
    };
    if (op === "load") {
      sheets.set(key, { sheet: args.sheet });
      return true;
    }
    if (op === "drop") {
      sheets.delete(key);
      return true;
    }
    if (op === "segs") {
      const e = sheets.get(key);
      if (!e) throw new Error("sheet not loaded");
      const S = e.sheet.segs,
        F = e.sheet.segF,
        n = S.length >> 2,
        out = [];
      for (let i = 0; i < n; i++) {
        if (F[i] & 4) continue;
        const x1 = S[i * 4],
          y1 = S[i * 4 + 1],
          x2 = S[i * 4 + 2],
          y2 = S[i * 4 + 3];
        if (Math.hypot(x2 - x1, y2 - y1) >= (args.minLen || 0))
          out.push(x1, y1, x2, y2);
      }
      return Float32Array.from(out);
    }
    if (op === "grids") {
      const e = sheets.get(key);
      if (!e) throw new Error("sheet not loaded");
      const { ceilingGrid } = await import("./ceilingGrid.js");
      return (args.rings || []).map((r) =>
        ceilingGrid(e.sheet, r, args.ftPerUnit),
      );
    }
    const st = ensure(key, args.ftPerUnit, args.options);
    const extra = {
      runs: slimRuns(st),
      tagCands: st.tagCands,
      tags: W.tagSummary(st),
    };
    if (op === "analyze") return { stats: st.stats, ...extra };
    if (op === "rooms")
      return {
        rooms: W.findRooms(st, { region: args.region }),
        calloutPts: st.calloutPts,
        stats: st.stats,
        doors: (st.doors || []).map(({ hx, hy, cx, cy, r }) => ({
          hx,
          hy,
          cx,
          cy,
          r,
        })),
        ...(args.faces ? { faces: W.wallFaces(st), dims: dimsOf(key) } : {}),
        ...extra,
      };
    if (op === "roomAt")
      return { room: W.roomAt(st, args.x, args.y), ...extra };
    throw new Error(`unknown op ${op}`);
  }

  return {
    get usingWorker() {
      return !!worker;
    },
    isLoaded: (pageNum) => loaded.has(pageNum),
    scaleOf: (pageNum) => loaded.get(pageNum)?.scale || null,
    checkOf: (pageNum) =>
      loaded.get(pageNum)?.check || checks.get(pageNum) || null,
    async load(pageNum, page) {
      if (loaded.has(pageNum)) return loaded.get(pageNum);
      const sheet = await extractSheet(pdfjsLib, page);
      const scale = detectScaleFromText(sheet.text);
      let check = checks.get(pageNum);
      if (!check) {
        try {
          check = checkSheetScale(sheet, scale);
        } catch {
          check = null;
        }
        checks.set(pageNum, check);
      }
      const info = {
        scale,
        check,
        text: sheet.text,
        width: sheet.width,
        height: sheet.height,
        segs: sheet.segs.length >> 2,
        isScan:
          sheet.imageArea > sheet.width * sheet.height * 0.5 &&
          sheet.segs.length >> 2 < 400,
      };
      const transfer = [
        sheet.segs.buffer,
        sheet.segL.buffer,
        sheet.rects.buffer,
        sheet.annSegs.buffer,
        sheet.segW.buffer,
        sheet.segF.buffer,
        sheet.segSub.buffer,
        sheet.subClosed.buffer,
        sheet.subCurve.buffer,
        sheet.subSize.buffer,
        sheet.subOrtho.buffer,
        sheet.smallShapes.buffer,
      ];
      await call("load", pageNum, { sheet }, worker ? transfer : []);
      loaded.set(pageNum, info);
      return info;
    },
    // scale check for one sheet without keeping its geometry loaded (the whole set is checked on upload)
    async checkScale(pageNum, page) {
      if (checks.has(pageNum)) return checks.get(pageNum);
      const sheet = await extractSheet(pdfjsLib, page);
      let check = null;
      try {
        check = checkSheetScale(sheet, detectScaleFromText(sheet.text));
      } catch {
        check = null;
      }
      checks.set(pageNum, check);
      return check;
    },
    analyze: (pageNum, ftPerUnit, options) =>
      call("analyze", pageNum, { ftPerUnit, options }),
    rooms: (pageNum, ftPerUnit, region, options, extra = {}) =>
      call("rooms", pageNum, { ftPerUnit, region, options, ...extra }),
    // ceiling grid inside each ring (RCP): [{ kind: "2x4"|"2x2"|"grid"|"none", spacing, label, guessed }]
    grids: (pageNum, ftPerUnit, rings) =>
      call("grids", pageNum, { ftPerUnit, rings }),
    // the drawing's straight lines [x1,y1,x2,y2,…] sheet units — snap targets while drawing / editing
    segs: (pageNum, minLen = 0) => call("segs", pageNum, { minLen }),
    roomAt: (pageNum, ftPerUnit, x, y, options) =>
      call("roomAt", pageNum, { ftPerUnit, x, y, options }),
    reset() {
      for (const k of loaded.keys()) call("drop", k, {}).catch(() => {});
      loaded.clear();
      checks.clear();
    },
    // forget one sheet (its revision changed): the next use reads it again from the PDF page it's given
    drop(pageNum) {
      if (loaded.has(pageNum)) call("drop", pageNum, {}).catch(() => {});
      loaded.delete(pageNum);
      checks.delete(pageNum);
    },
    terminate() {
      worker?.terminate();
      worker = null;
    },
  };
}
